import { ViewResponse } from '../../../models/viewResponse/ViewResponse';
import { BaseWidgetOptions } from '../../../models/Widget/BaseWidgetOptions';
import { processEventsArray } from './helpers/processEventsArray';
import { FilterDefinition } from '../../../components/Filters/models/FilterDefinition';

/**
 * Создаёт независимую копию конфигурации и заменяет
 * идентификатор текущего экземпляра во всех поддерживаемых
 * местах конфигурации.
 */
export function fillIdentity(config: ViewResponse, newId: string | null): ViewResponse {
  const result: ViewResponse = JSON.parse(JSON.stringify(config));

  if (!Array.isArray(result.areasConfig)) {
    return result;
  }

  result.areasConfig = result.areasConfig.map((area) => ({
    ...area,
    widget: processWidget(area.widget, newId),
  }));

  return result;
}

/**
 * Обрабатывает конфигурацию отдельного виджета.
 *
 * Обновляются все известные свойства, содержащие
 * identity текущей сущности или события, в которых
 * identity используется для выполнения действий.
 */
function processWidget(widget: BaseWidgetOptions, newId: string | null): BaseWidgetOptions {
  if (!widget) {
    return widget;
  }

  const result: any = { ...widget };

  if (Array.isArray(result.events)) {
    result.events = processEventsArray(result.events, newId);
  }

  if (result.innerGrid) {
    result.innerGrid = fillIdentity(result.innerGrid, newId);
  }

  if (result.collection) {
    result.collection = {
      ...result.collection,
      id: newId,
    };
  }

  if (result.collectionProcessing) {
    result.collectionProcessing = processCollectionProcessing(result.collectionProcessing, newId);
  }

  if (result.columnContext) {
    result.columnContext = processContextWithCollectionProcessing(result.columnContext, newId);
  }

  if (result.rowContext) {
    result.rowContext = processContextWithCollectionProcessing(result.rowContext, newId);
  }

  if (result.links?.collection) {
    result.links = {
      ...result.links,
      collection: {
        ...result.links.collection,
        id: newId,
      },
    };
  }

  if (result.cellButton?.events) {
    result.cellButton = {
      ...result.cellButton,
      events: processEventsArray(result.cellButton.events, newId),
    };
  }

  const eventCollections = ['buttons', 'tabs', 'selectionMenuItems', 'columns'] as const;

  eventCollections.forEach((property) => {
    if (!Array.isArray(result[property])) {
      return;
    }

    result[property] = result[property].map((item: any) => {
      if (!item?.events) {
        return item;
      }

      return {
        ...item,
        events: processEventsArray(item.events, newId),
      };
    });
  });

  if (Array.isArray(result.filters)) {
    result.filters = result.filters.map((filter: FilterDefinition) => {
      if (!filter.referenceDataSource || !('id' in filter.referenceDataSource)) {
        return filter;
      }

      return {
        ...filter,
        referenceDataSource: {
          ...filter.referenceDataSource,
          id: newId,
        },
      };
    });
  }

  return result;
}

/**
 * Заменяет identity внутри `collectionProcessing`.
 * Обрабатываются контексты представления и коллекции.
 */
function processCollectionProcessing(collectionProcessing: any, newId: string | null): any {
  const result = {
    ...collectionProcessing,
  };

  if (result.viewContext) {
    result.viewContext = {
      ...result.viewContext,
      id: newId,
    };
  }

  if (result.collection) {
    result.collection = {
      ...result.collection,
      id: newId,
    };
  }

  return result;
}

/**
 * Обновляет identity внутри `columnContext` или `rowContext`.
 */
function processContextWithCollectionProcessing(context: any, newId: string | null): any {
  if (!context.collectionProcessing) {
    return context;
  }

  return {
    ...context,
    collectionProcessing: processCollectionProcessing(context.collectionProcessing, newId),
  };
}
